import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "next/navigation";

import { getProductsByOrgId } from "@/actions/product-facet";
import { getProductsHubSummaries } from "@/actions/products-hub";
import type { ProductFacetModel } from "@/api";
import { queryKeys } from "@/constants/query";
import { isValidOrgId } from "@/lib/utils";
import type { ProductHubCardSummary } from "@/types/product-intelligence";

function sortProductsByName(
  products: readonly ProductFacetModel[],
): ProductFacetModel[] {
  return [...products].sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? "", undefined, {
      sensitivity: "base",
    }),
  );
}

export function useProductsByOrgId(orgIdOverride?: string): {
  products: ProductFacetModel[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
} {
  const { orgId: routeOrgId } = useParams();
  const orgId = orgIdOverride ?? (routeOrgId as string | undefined);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: queryKeys.productsByOrgId(orgId ?? ""),
    queryFn: () => getProductsByOrgId(orgId as string),
    enabled: isValidOrgId(orgId),
  });

  const products = useMemo(
    () => sortProductsByName(data ?? []),
    [data],
  );

  return {
    products,
    isLoading,
    isError,
    error: error as Error | null,
  };
}

export function useProductsHubSummaries(): {
  summaries: ProductHubCardSummary[];
  isLoading: boolean;
  isError: boolean;
} {
  const { orgId } = useParams();
  const validOrgId = isValidOrgId(orgId as string | undefined)
    ? (orgId as string)
    : undefined;

  const { data = [], isLoading, isError } = useQuery({
    queryKey: queryKeys.productsHubSummaries(validOrgId ?? ""),
    queryFn: (): Promise<ProductHubCardSummary[]> =>
      getProductsHubSummaries(validOrgId!),
    enabled: !!validOrgId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  return {
    summaries: data,
    isLoading,
    isError,
  };
}
